/**
 * Blocking dialog shown to existing users when policy versions have been bumped.
 *
 * Users cannot dismiss the dialog by clicking outside, pressing Escape, or via a close button --
 * the only paths forward are accepting the updated policies or signing out. The dialog reads
 * its visibility from `policyAcceptanceState.requiresAcceptance`, which guarantees it disappears
 * automatically once acceptance is recorded (or the user signs out).
 */

import { PRIVACY_URL, TERMS_URL } from '@elara/domain/policy-versions';

import { usePolicyAcceptanceState, useSessionState } from '#states/hooks.js';
import { Alert, Button, Dialog, DialogContent, DialogDescription, DialogTitle } from '#ui/index.js';

export function PolicyAcceptanceDialog() {
  const policyAcceptanceState = usePolicyAcceptanceState();
  const sessionState = useSessionState();

  const open = policyAcceptanceState.requiresAcceptance;
  const isAccepting = policyAcceptanceState.isAccepting;
  const error = policyAcceptanceState.error;

  const handleAccept = async () => {
    await policyAcceptanceState.accept();
  };

  const handleSignOut = async () => {
    await sessionState.logout();
  };

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle>We’ve updated our policies</DialogTitle>
        <DialogDescription>
          Please review and accept our latest{' '}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
            Privacy Policy
          </a>{' '}
          to continue using Elara.
        </DialogDescription>

        {error && <Alert variant="destructive">{error}</Alert>}

        <div className="flex flex-col gap-3 mt-2">
          <Button size="lg" onClick={handleAccept} disabled={isAccepting}>
            {isAccepting ? 'Saving…' : 'Accept and continue'}
          </Button>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={isAccepting}
            className="text-sm text-muted-foreground hover:text-foreground hover:underline disabled:opacity-50"
          >
            Sign out instead
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
